import React, { useEffect } from 'react';
import styles from './About.module.css';

const values = [
  {
    name: 'Innovation',
    desc: 'We foster a culture of creativity and continuous improvement, embracing new ideas and technologies to provide cutting-edge solutions.',
  },
  {
    name: 'Integrity',
    desc: 'We conduct our business with honesty and transparency, building trust with our clients and partners through ethical practices.',
  },
  {
    name: 'Customer-centricity',
    desc: "We prioritize our client's needs, actively listening to their concerns and delivering solutions that align with their goals.",
  },
  {
    name: 'Collaboration',
    desc: 'We believe in teamwork, working together with our clients and within our organization to achieve shared objectives and deliver exceptional results.',
  },
  {
    name: 'Excellence',
    desc: 'We strive for excellence in everything we do, ensuring high-quality outcomes for our clients through rigorous standards and best practices.',
  },
  {
    name: 'Adaptability',
    desc: "In an ever-changing technological landscape, we remain flexible and open to change, enabling us to pivot and respond effectively to our client's evolving needs.",
  },
];

const journey = {
  intro:
    'Founded in 2024, TRANSVUE Solutions has evolved from a small startup to a leading IT services provider. Our journey has been marked by numerous successful projects, partnerships, and milestones that reflect our dedication to innovation and customer satisfaction. We take pride in our ability to adapt to the changing technological landscape and continuously deliver value to our clients.',
  services: [
    {
      title: 'Software Development',
      text: 'Custom software solutions tailored to meet specific business needs.',
    },
    {
      title: 'IT Consultancy',
      text: 'Expert advice and strategic planning to help organizations navigate the complexities of technology.',
    },
    {
      title: 'Cloud Solutions',
      text: 'Cloud computing services that enhance scalability, security, and efficiency.',
    },
    {
      title: 'Data Analytics',
      text: 'Data-driven insights to empower businesses to make informed decisions.',
    },
    {
      title: 'Cybersecurity',
      text: 'Robust security solutions designed to protect businesses from digital threats.',
    },
  ],
};

const team = [
  {
    name: 'Purushottam Punamchand Singhal',
    role: 'CEO',
    img: '/images/team/purushottam.jpeg',
    linkedin: 'https://www.linkedin.com/in/purushottam-punamchand-singhal-b657079b/',
  },
  {
    name: 'Uttam Singhal',
    role: 'MD',
    img: '/images/team/uttam.jpeg',
    linkedin: 'https://www.linkedin.com/in/uttamsinghal1985/',
  },
  {
    name: 'Siddharth Kashyap',
    role: 'CFO',
    img: '/images/team/sid.jpg',
    linkedin: 'https://www.linkedin.com/in/siddharth-kashyap-profile/',
  },
  {
    name: 'Jagdish Prasad Singh',
    role: 'Senior Software Engineer',
    img: '/images/team/jagdish.jpg',
    linkedin: 'https://www.linkedin.com/in/jagdish-singh-092919244/',
  },
  {
    name: 'Piyush Garg',
    role: 'Software Engineer',
    img: '/images/team/piyush.jpg',
    linkedin: 'https://www.linkedin.com/in/piyushgarg20/',
  },
  {
    name: 'Vicky Kumar Singh',
    role: 'Software Engineer',
    img: '/images/team/vicky.png',
    linkedin: 'https://www.linkedin.com/in/vicky-kumar-singh-691a92222/',
  },
];

export default function About() {
  useEffect(() => {
    const observerOptions = {
      root: null,
      rootMargin: '0px',
      threshold: 0.12,
    };

    const handleIntersect = (entries, observer) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          entry.target.classList.add(styles.isVisible);
          observer.unobserve(entry.target);
        }
      });
    };

    const observer = new IntersectionObserver(handleIntersect, observerOptions);
    const revealElements = document.querySelectorAll('[data-reveal]');
    
    revealElements.forEach((el) => observer.observe(el));

    return () => {
      revealElements.forEach((el) => observer.unobserve(el));
    };
  }, []);

  return (
    <div className={styles.page}>
      
      <section className={`${styles.section} ${styles.darkSection}`}>
        <div className="container">
          <div className={styles.whoGrid}>
            <div className={`${styles.whoLeft} ${styles.reveal}`} data-reveal>
              <h1 className="section-title">Who We Are</h1>
              <div className={styles.avatar}>
                <img
                  src="/images/others/top.png"
                  alt="Our Team"
                  className={styles.avatarImg}
                />
              </div>
            </div>
            <div className={`${styles.whoRight} ${styles.reveal}`} data-reveal style={{ transitionDelay: '0.15s' }}>
              <p>
                TSIL is a results-driven digital agency founded by a team of passionate
                technologists, designers, and strategists who believe great digital products
                can transform businesses.
              </p>
              <p>
                We partner with ambitious companies to build scalable web applications,
                elegant mobile apps, and brand identities that stand out in crowded markets.
              </p>
            </div>
          </div>
        </div>


        <div className={`${styles.sectionWave} ${styles.waveToLight}`} aria-hidden="true">
          <svg viewBox="0 0 1440 100" preserveAspectRatio="none">
            <path d="M0,0 C480,100 960,0 1440,80 L1440,100 L0,100 Z" />
          </svg>
        </div>
      </section>

      <section className={`${styles.section} ${styles.lightSection}`}>
        <div className="container">
          <div className={styles.visionGrid}>
            <div className={styles.reveal} data-reveal>
              <h2 className="section-title">Our Vision</h2>
              <p>
                We strive to be at the forefront of technological innovation, helping
                businesses of all sizes harness the power of digital transformation. Our goal
                is to make world-class digital solutions accessible, affordable, and impactful
                for every organisation we serve.
              </p>
            </div>
            <div className={`${styles.visionVisual} ${styles.reveal}`} data-reveal style={{ transitionDelay: '0.15s' }}>
              <img
                src="/images/others/vision.png"
                alt="Our Vision"
                className={styles.sectionImage}
              />
            </div>
          </div>
        </div>


        <div className={`${styles.sectionWave} ${styles.waveToDark}`} aria-hidden="true">
          <svg viewBox="0 0 1440 100" preserveAspectRatio="none">
            <path d="M0,0 C480,100 960,0 1440,80 L1440,100 L0,100 Z" />
          </svg>
        </div>
      </section>

      
      <section className={`${styles.section} ${styles.darkSection}`}>
        <div className="container">
          <div className={styles.missionGrid}>
            <div className={`${styles.missionVisual} ${styles.reveal}`} data-reveal>
              <img
                src="/images/others/mission.png"
                alt="Our Mission"
                className={styles.sectionImage}
              />
            </div>
            <div className={styles.reveal} data-reveal style={{ transitionDelay: '0.15s' }}>
              <h2 className="section-title">Our Mission</h2>
              <p>
                Our mission is to deliver high-quality and innovative digital solutions that
                empower businesses to thrive in the digital age. We are committed to providing
                exceptional service, fostering long-term partnerships and consistently
                exceeding client expectations through expertise and dedication.
              </p>
            </div>
          </div>
        </div>


        <div className={`${styles.sectionWave} ${styles.waveToLight}`} aria-hidden="true">
          <svg viewBox="0 0 1440 100" preserveAspectRatio="none">
            <path d="M0,0 C480,100 960,0 1440,80 L1440,100 L0,100 Z" />
          </svg>
        </div>
      </section>


      <section className={`${styles.section} ${styles.lightSection}`}>
        <div className="container">
          <h2 className="section-title" style={{ textAlign: 'center', marginBottom: '40px' }}>Our Values</h2>
          <div className={styles.valuesGrid}>
            {values.map((v, i) => (
              <div
                key={v.name}
                className={`${styles.valueCard} ${styles.cardWithWave} ${styles.reveal}`}
                data-reveal
                style={{ transitionDelay: `${i * 0.08}s` }}
              >
                <h4 className={styles.valueCardTitle}>{v.name}:</h4>
                <p>{v.desc}</p>
              </div>
            ))}
          </div>
        </div>


        <div className={`${styles.sectionWave} ${styles.waveToDark}`} aria-hidden="true">
          <svg viewBox="0 0 1440 100" preserveAspectRatio="none">
            <path d="M0,0 C480,100 960,0 1440,80 L1440,100 L0,100 Z" />
          </svg>
        </div>
      </section>


      <section className={`${styles.section} ${styles.darkSection}`}>
        <div className="container">
          <h2 className={styles.journeyHeading}>OUR JOURNEY</h2>
          <div className={styles.journeyLine}></div>
          <div className={`${styles.journeyCard} ${styles.reveal}`} data-reveal>
            <p className={styles.journeyIntro}>{journey.intro}</p>
            <div className={styles.journeyServices}>
              {journey.services.map((item) => (
                <p key={item.title}>
                  <strong>{item.title}:</strong> {item.text}
                </p>
              ))}
            </div>
          </div>
        </div>


        <div className={`${styles.sectionWave} ${styles.waveToLight}`} aria-hidden="true">
          <svg viewBox="0 0 1440 100" preserveAspectRatio="none">
            <path d="M0,0 C480,100 960,0 1440,80 L1440,100 L0,100 Z" />
          </svg>
        </div>
      </section>

  

      <section className={`${styles.section} ${styles.lightSection}`} style={{ paddingBottom: '120px' }}>
        <div className="container">
          <h2 className="section-title" style={{ textAlign: 'center' }}>
            Meet Our Team
          </h2>
          <p className={styles.teamSubtitle}>
            The people behind every product we ship.
          </p>
          <div className={styles.teamGrid}>
            {team.map((member, i) => (
              <div
                key={member.name}
                className={`${styles.teamCard} ${styles.reveal}`}
                data-reveal
                style={{ transitionDelay: `${i * 0.12}s` }}
              >
                <div className={styles.teamImageWrap}>
                  <img
                    src={member.img}
                    alt={member.name}
                    className={styles.teamImage}
                  />
                  <div className={styles.teamOverlay}>
                    <a
                      href={member.linkedin}
                      target="_blank"
                      rel="noopener noreferrer"
                      className={styles.teamLinkedin}
                      aria-label={`${member.name} on LinkedIn`}
                    >
                      <svg viewBox="0 0 24 24" width="20" height="20" fill="currentColor" aria-hidden="true">
                        <path d="M20.45 20.45h-3.55v-5.57c0-1.33-.02-3.04-1.85-3.04-1.85 0-2.14 1.45-2.14 2.94v5.67H9.36V9h3.41v1.56h.05c.48-.9 1.64-1.85 3.38-1.85 3.6 0 4.27 2.37 4.27 5.46v6.28zM5.34 7.43a2.06 2.06 0 1 1 0-4.12 2.06 2.06 0 0 1 0 4.12zM7.12 20.45H3.56V9h3.56v11.45z" />
                      </svg>
                    </a>
                  </div>
                </div>
                <div className={styles.teamInfo}>
                  <h4 className={styles.teamName}>{member.name}</h4>
                  <span className={styles.teamRole}>{member.role}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

    </div>
  );
}